import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Plus, ArrowDownUp, GraduationCap, Briefcase, MapPin, Pencil, Trash2, Lock } from 'lucide-react';
import { Formation, Skill } from '../types';
import { parseDateStr, localized, mediaUrl, hasRichContent } from '../utils';
import { BACKEND_URL } from '../config';
import PageMeta from '../components/PageMeta';
import SkillBadge from '../components/SkillBadge';
import SkeletonImage from '../components/SkeletonImage';
import { RowsSkeleton } from '../components/Skeletons';

interface FormationsProps {
  authenticated: boolean;
}

type Kind = 'all' | 'experience' | 'study';
type Assigned = { id: number; strid: string; title: string; skills: Skill[] };

const label = 'text-[11px] font-semibold uppercase tracking-[0.08em] text-zinc-500 mb-2.5';

export default function Formations({ authenticated }: FormationsProps) {
  const [formations, setFormations] = useState<Formation[]>([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState<string | null>(() => decodeURIComponent(window.location.hash.slice(1)) || null);
  const [kind, setKind] = useState<Kind>('all');
  const [sort, setSort] = useState<'desc' | 'asc'>('desc');
  const [imageExistsMap, setImageExistsMap] = useState<Record<string, boolean>>({});
  const [assignedProjects, setAssignedProjects] = useState<Assigned[]>([]);
  const detailRef = useRef<HTMLElement>(null);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();

  const headers = (): HeadersInit => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/formations`, { headers: headers() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => { setFormations(data); setLoading(false); })
      .catch(() => { toast.error(String(t('error_loading_formations'))); setLoading(false); });
  }, [t]);

  useEffect(() => {
    const checkImages = async () => {
      const entries = await Promise.all(
        formations.filter(f => f.logo).map(async f => {
          const res = await fetch(`${BACKEND_URL}/api/formations/check-file/${encodeURIComponent(`${f.strid}/${f.logo}`)}`);
          return [f.strid, (await res.json()).exists] as [string, boolean];
        })
      );
      setImageExistsMap(Object.fromEntries(entries));
    };
    if (formations.length > 0) checkImages();
  }, [formations]);

  const shown = [...formations]
    .filter(f => kind === 'all' || (kind === 'study') === !!f.is_study)
    .sort((a, b) => (sort === 'desc' ? 1 : -1) * (parseDateStr(b.start_date) - parseDateStr(a.start_date)));
  const cur = shown.find(f => f.strid === sel) ?? shown[0];
  const curId = cur?.strid;

  useEffect(() => {
    if (!curId) { setAssignedProjects([]); return; }
    let stale = false;
    setAssignedProjects([]);
    fetch(`${BACKEND_URL}/api/formations/assignation/${curId}`, { headers: headers() })
      .then(r => r.json())
      .then(data => { if (!stale) setAssignedProjects(Array.isArray(data) ? data : []); })
      .catch(console.error);
    return () => { stale = true; };
  }, [curId]);

  const select = (strid: string) => {
    setSel(strid);
    window.history.replaceState(null, '', `#${strid}`);
    // Below lg the detail sits under the list: bring it into view
    if (window.matchMedia('(max-width: 1023px)').matches) detailRef.current?.scrollIntoView({ behavior: 'smooth' });
    else detailRef.current?.scrollTo({ top: 0 }); // lg: the detail scrolls on its own
  };

  const handleDelete = (strid: string) => {
    if (!window.confirm(t('confirm_delete_formation') as string)) return;
    fetch(`${BACKEND_URL}/api/formations/${strid}`, { method: 'DELETE', headers: headers() })
      .then(res => {
        if (!res.ok) throw new Error();
        setFormations(prev => prev.filter(f => f.strid !== strid));
        setSel(null);
        window.history.replaceState(null, '', window.location.pathname);
      })
      .catch(() => toast.error(String(t('error_saving'))));
  };

  const titleOf = (f: Formation) => localized(f.is_study ? f.degree : f.position, lang) || f.name;
  const datesOf = (f: Formation) => `${f.start_date} → ${f.ongoing ? t('on_going') : f.end_date}`;
  const tag = (f: Formation) => (
    <span className={`inline-flex items-center h-7 px-3 rounded-full text-xs font-semibold ${f.is_study ? 'bg-primary/10 text-primary' : 'bg-primary text-white'}`}>
      {f.is_study ? t('formations') : t('experiences_title')}
    </span>
  );
  const logo = (f: Formation, size: string) => (
    <div className={`${size} rounded-xl overflow-hidden bg-surface ring-1 ring-line flex-shrink-0 flex items-center justify-center`}>
      {imageExistsMap[f.strid]
        ? <SkeletonImage src={mediaUrl(f.logo, `${BACKEND_URL}/formations/${f.strid}`) ?? ''} alt="" className="object-cover" />
        : f.is_study ? <GraduationCap size={20} className="text-primary-light" /> : <Briefcase size={20} className="text-primary-light" />}
    </div>
  );

  const pills: [Kind, string, number][] = [
    ['all', String(t('all')), formations.length],
    ['experience', String(t('experiences_title')), formations.filter(f => !f.is_study).length],
    ['study', String(t('formations')), formations.filter(f => f.is_study).length],
  ];
  const skills = cur ? cur.skills.filter(s => !s.from_project) // inherited skills show on their projects
    .sort((a, b) => localized(a.name, lang).localeCompare(localized(b.name, lang), lang)) : [];
  const richKey = cur?.is_study ? 'courses' : 'tasks';

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20">
      <PageMeta title={t('formations') as string} />
      <h1 className="mb-8 font-display font-extrabold tracking-[-0.04em] text-5xl sm:text-[64px] leading-none">{t('my_career')}</h1>

      {loading && <RowsSkeleton />}

      {!loading && (
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-10 items-start">
          <nav aria-label={t('my_career') as string} className="w-full lg:w-[380px] lg:flex-shrink-0 lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:overscroll-contain flex flex-col gap-2">
            <div role="group" className="flex flex-wrap items-center gap-1.5 mb-2">
              {pills.map(([id, text, n]) => (
                <button key={id} type="button" onClick={() => setKind(id)} aria-pressed={kind === id}
                  className={`h-11 px-3.5 rounded-full text-[13px] font-semibold border-[1.5px] transition-colors ${kind === id ? 'bg-ink text-base border-ink' : 'border-line text-ink hover:bg-surface'}`}>
                  {text} <span className="opacity-70 font-medium">{n}</span>
                </button>
              ))}
              <button type="button" onClick={() => setSort(s => (s === 'desc' ? 'asc' : 'desc'))}
                className="h-11 px-2 inline-flex items-center gap-1.5 text-[13px] text-zinc-500 hover:text-primary-light transition-colors ml-auto">
                <ArrowDownUp size={14} /> {sort === 'desc' ? t('sort_newest') : t('sort_oldest')}
              </button>
            </div>
            {authenticated && (
              <div className="flex flex-wrap gap-4 mb-2">
                <button type="button" onClick={() => navigate('/formations/new', { state: { createStudy: false } })} className="inline-flex items-center gap-1.5 h-9 text-sm font-medium text-primary-light"><Plus size={16} /> {t('experiences_title')}</button>
                <button type="button" onClick={() => navigate('/formations/new', { state: { createStudy: true } })} className="inline-flex items-center gap-1.5 h-9 text-sm font-medium text-primary-light"><Plus size={16} /> {t('formations')}</button>
              </div>
            )}
            {shown.length === 0 && <p className="text-zinc-500 py-8 text-center">{t('no_formation')}</p>}
            {shown.map(f => (
              <button key={f.strid} id={f.strid} type="button" onClick={() => select(f.strid)} aria-current={f.strid === curId}
                className={`flex items-center gap-3.5 w-full min-h-[76px] px-3.5 py-3 rounded-2xl border-[1.5px] text-left transition-colors ${f.strid === curId ? 'bg-surface border-primary' : 'border-line hover:bg-surface'}`}>
                {logo(f, 'w-11 h-11')}
                <span className="flex-1 min-w-0">
                  <span className="block font-display font-bold text-base leading-tight text-ink truncate">{titleOf(f)}</span>
                  {titleOf(f) !== f.name && <span className="block text-[13px] text-muted truncate mt-0.5">{f.name}</span>}
                  <span className="block font-mono text-xs text-muted mt-0.5">{datesOf(f)}</span>
                </span>
                <span className={`w-2.5 h-2.5 rounded-full border-2 border-primary flex-shrink-0 ${f.is_study ? '' : 'bg-primary'}`} title={String(f.is_study ? t('formations') : t('experiences_title'))} />
              </button>
            ))}
          </nav>

          {cur && (
            <section ref={detailRef} aria-live="polite" className="flex-1 min-w-0 w-full border border-line rounded-3xl p-6 sm:p-9 scroll-mt-24 lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:overscroll-contain">
              <div className="flex flex-wrap items-center gap-5 sm:gap-6">
                {logo(cur, 'w-[88px] h-[88px]')}
                <div className="flex-1 basis-64 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    {tag(cur)}
                    {localized(cur.type, lang) && <span className="text-sm text-muted">{localized(cur.type, lang)}</span>}
                  </div>
                  <h2 className="mt-2.5 font-display font-extrabold tracking-[-0.03em] text-3xl sm:text-4xl leading-[1.05] text-ink">{titleOf(cur)}</h2>
                  {titleOf(cur) !== cur.name && <p className="mt-1.5 text-base text-muted">{cur.name}</p>}
                </div>
                {authenticated && (
                  <div className="flex items-center gap-1 self-start">
                    <Link to={`/formations/edit/${cur.strid}`} aria-label={t('edit') as string} className="p-2.5 rounded-lg text-zinc-500 hover:text-primary-light hover:bg-primary/10 transition-colors"><Pencil size={16} /></Link>
                    <button type="button" onClick={() => handleDelete(cur.strid)} aria-label={t('delete') as string} className="p-2.5 rounded-lg text-zinc-500 hover:text-red-500 hover:bg-red-500/10 transition-colors"><Trash2 size={16} /></button>
                  </div>
                )}
              </div>

              <div className="mt-5 py-3.5 border-y border-line flex flex-wrap gap-x-8 gap-y-3">
                <div><p className={`${label} !mb-1.5`}>{t('dates')}</p><p className="font-medium text-ink">{datesOf(cur)}</p></div>
                {cur.localisation && <div><p className={`${label} !mb-1.5`}>{t('location_placeholder')}</p><p className="font-medium text-ink inline-flex items-center gap-1.5"><MapPin size={14} className="text-zinc-500" />{cur.localisation}</p></div>}
              </div>

              {hasRichContent(cur[richKey], lang) && (
                <div className="mt-7">
                  <p className={label}>{t(richKey)}</p>
                  <div className="prose-content text-base leading-relaxed" dangerouslySetInnerHTML={{ __html: localized(cur[richKey], lang) }} />
                </div>
              )}

              {skills.length > 0 && (
                <div className="mt-7">
                  <p className={label}>{t('skills_skills')}</p>
                  <div className="flex flex-wrap gap-2">{skills.map(s => <SkillBadge key={s.id} skill={s} />)}</div>
                </div>
              )}

              {assignedProjects.length > 0 && (
                <div className="mt-7">
                  <p className={label}>{t('projects')}</p>
                  <div className="flex flex-wrap gap-2">
                    {[...assignedProjects].sort((a, b) => a.title.localeCompare(b.title, lang)).map(p => (
                      <Link key={p.strid} to={`/projects/${p.strid}`} className="inline-flex items-center min-h-[34px] px-3.5 rounded-full border border-primary text-primary-light text-sm font-medium hover:bg-surface transition-colors">{p.title} →</Link>
                    ))}
                  </div>
                </div>
              )}

              {authenticated && (cur.private_links || []).length > 0 && (
                <div className="mt-7 bg-surface rounded-xl p-4 border border-line">
                  <p className={`${label} flex items-center gap-1.5`}><Lock size={12} /> {t('private_links')}</p>
                  <div className="flex flex-wrap gap-2">
                    {[...(cur.private_links || [])].map((link, idx) => (
                      <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs border border-zinc-700 text-zinc-200 px-3 py-1.5 rounded-lg hover:text-ink transition-all font-medium">
                        <Lock size={11} />{localized(link.title, lang) || link.url}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}
        </div>
      )}
    </div>
  );
}
